import React, { useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  MdOutlineLocalShipping,
  MdOutlineMap,
  MdOutlineTune,
  MdOutlineTwoWheeler,
  MdPublic,
  MdRefresh,
} from "react-icons/md";
import Card from "components/card";
import { ldAdminApi } from "./api";

const EMPTY = {
  scope: "global",
  scopeId: null,
  baseFee: 30,
  perKmFee: 8,
  maxServiceRadiusKm: 10,
  linkExpiryMinutes: 10,
  riderSearchWindowSec: 60,
  riderSearchRadiusKm: 5,
  extraDistanceThresholdKm: 2,
  stalledOutForDeliveryThresholdMin: 60,
  minOrderValue: 0,
  active: true,
};

// Field groups drive the whole form — keep units/help next to each field.
const GROUPS = [
  {
    title: "Pricing",
    icon: MdOutlineLocalShipping,
    fields: [
      { k: "baseFee", label: "Base fee", prefix: "₹", help: "Flat fee charged per delivery" },
      { k: "perKmFee", label: "Per km fee", prefix: "₹", help: "Added for each km of distance" },
      { k: "minOrderValue", label: "Min order value", prefix: "₹", help: "0 = no minimum" },
    ],
  },
  {
    title: "Service area",
    icon: MdOutlineMap,
    fields: [
      { k: "maxServiceRadiusKm", label: "Max service radius", suffix: "km", help: "Beyond this, delivery is unavailable" },
      { k: "extraDistanceThresholdKm", label: "Extra distance threshold", suffix: "km", help: "Trigger surcharge approval past this" },
    ],
  },
  {
    title: "Rider matching",
    icon: MdOutlineTwoWheeler,
    fields: [
      { k: "riderSearchRadiusKm", label: "Rider search radius", suffix: "km", help: "How far to look for riders" },
      { k: "riderSearchWindowSec", label: "Rider search window", suffix: "sec", help: "How long to keep searching" },
    ],
  },
  {
    title: "Timing",
    icon: MdOutlineTune,
    fields: [
      { k: "linkExpiryMinutes", label: "Link expiry", suffix: "min", help: "Customer location link lifetime" },
      { k: "stalledOutForDeliveryThresholdMin", label: "Stalled OFD alert", suffix: "min", help: "Flag deliveries stuck this long" },
    ],
  },
];

export default function Pricing() {
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loaded = useRef(EMPTY); // last value fetched from server, for Reset

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ldAdminApi.listConfigs();
      const list = res.configs || [];
      const current = list.find((c) => c.scope === "global") || list[0];
      const next = current ? { ...EMPTY, ...current } : EMPTY;
      loaded.current = next;
      setDraft(next);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = { ...draft, scope: "global", scopeId: null };
      await ldAdminApi.upsertConfig(body);
      toast.success("Configuration saved");
      refresh();
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setError(null);
    setDraft(loaded.current);
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(loaded.current);

  return (
    <div className="mx-auto mt-3 max-w-3xl">
      <ToastContainer position="bottom-right" autoClose={2500} hideProgressBar newestOnTop />

      {/* Page header */}
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-500 text-white">
              <MdOutlineLocalShipping className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
              Parcel Delivery Configuration
            </h1>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Global fees, service radius and rider-matching rules applied to every
            parcel delivery.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          title="Reload from server"
          className="flex items-center gap-1 rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
        >
          <MdRefresh className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Reload
        </button>
      </div>

      <Card extra="p-6">
        {/* Status row */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-5 dark:border-white/10">
          <span className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-600 dark:bg-white/10 dark:text-gray-200">
            <MdPublic className="h-4 w-4" /> Global config
          </span>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              {draft.active ? "Live" : "Disabled"}
            </span>
            <Switch on={draft.active} onChange={(v) => setDraft({ ...draft, active: v })} />
          </div>
        </div>

        {loading ? (
          <div className="space-y-5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-white/5" />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {GROUPS.map((g) => {
              const GIcon = g.icon;
              return (
                <div key={g.title}>
                  <div className="mb-2 flex items-center gap-2">
                    <GIcon className="h-4 w-4 text-brand-500" />
                    <h3 className="text-sm font-semibold text-navy-700 dark:text-white">
                      {g.title}
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {g.fields.map((f) => (
                      <NumField
                        key={f.k}
                        field={f}
                        value={draft[f.k]}
                        onChange={(v) => setDraft({ ...draft, [f.k]: v })}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {error && (
          <div className="mt-5 rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="mt-6 flex items-center gap-3 border-t border-gray-100 pt-5 dark:border-white/10">
          <button
            disabled={saving || loading || !dirty}
            onClick={save}
            className="rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
          <button
            disabled={!dirty || saving}
            onClick={reset}
            className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
          >
            Reset
          </button>
          {dirty && !loading && (
            <span className="text-xs text-amber-600">Unsaved changes</span>
          )}
        </div>
      </Card>
    </div>
  );
}

function NumField({ field, value, onChange }) {
  const { label, prefix, suffix, help } = field;
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </span>
      <div className="mt-1.5 flex items-center rounded-xl border border-gray-200 bg-white/0 px-3 transition focus-within:border-brand-500 dark:border-white/10">
        {prefix && <span className="text-sm text-gray-400">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full bg-transparent px-1.5 py-2.5 text-sm text-navy-700 outline-none dark:text-white"
        />
        {suffix && <span className="text-xs text-gray-400">{suffix}</span>}
      </div>
      {help && <p className="mt-1 text-[11px] text-gray-400">{help}</p>}
    </label>
  );
}

function Switch({ on, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`relative h-6 w-11 rounded-full transition ${
        on ? "bg-brand-500" : "bg-gray-300 dark:bg-white/20"
      }`}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
          on ? "left-[22px]" : "left-0.5"
        }`}
      />
    </button>
  );
}
